import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  type ApplicantAnswers,
  ApplicantAnswersSchema,
  parseApplicantAnswers,
} from '@/apply/answers';
import type { CustomAnswerRow } from '@/components/settings/ApplicationAnswers';
import { useNextScanMinutes } from '@/components/ui/ScheduleActivityBar';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import { useUploadResume } from '@/hooks/useUploadResume';
import { type Profile, profileQueryOptions } from '@/lib/api';
import { RESUME_ACCEPT, splitCsv } from '@/lib/utils';
import type { ShellView } from '@/providers/dashboard/ShellContext';

export interface ScraperSource {
  id: string;
  label: string;
  notes: string;
  on: boolean;
  url: string;
}

const SCRAPER_SOURCES: ScraperSource[] = [
  {
    id: 'working_nomads',
    label: 'Working Nomads',
    notes: 'remote, $$',
    on: true,
    url: 'workingnomads.com',
  },
  {
    id: 'remote100k',
    label: 'Remote > $100K',
    notes: 'high salary filter',
    on: true,
    url: 'remote100k.com',
  },
  {
    id: 'levelsfyi',
    label: 'Levels.fyi',
    notes: 'tech comp',
    on: true,
    url: 'levels.fyi',
  },
  {
    id: 'jobgether',
    label: 'Jobgether',
    notes: 'remote-first',
    on: true,
    url: 'jobgether.com',
  },
  {
    id: 'wellfound',
    label: 'Wellfound',
    notes: 'startup',
    on: true,
    url: 'wellfound.com',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    notes: 'requires session cookie',
    on: true,
    url: 'linkedin.com/jobs',
  },
];

const DEFAULT_SCORING_MODEL = 'claude-haiku-4-5-20251001';
const DEFAULT_TAILORING_MODEL = 'claude-sonnet-4-6';
const DEFAULT_MATCH_THRESHOLD = 70;
const DEFAULT_PEAK_MIN = 30;
const DEFAULT_OFFPEAK_MIN = 180;
const DEFAULT_MAX_SCORE = 100;
const DEFAULT_MAX_TAILOR = 50;
const DEFAULT_PEAK_START_HOUR = 8;
const DEFAULT_PEAK_END_HOUR = 20;
const DEFAULT_TIMEZONE = 'America/Chicago';

const parseDisabledScrapers = (raw: string | null): Set<string> => {
  if (!raw) {
    return new Set();
  }

  try {
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

const applyDisabledScrapers = (
  sources: ScraperSource[],
  disabled: Set<string>,
): ScraperSource[] =>
  sources.map((source) => ({ ...source, on: !disabled.has(source.id) }));

const parseArrayField = (raw: string | null): string => {
  if (!raw) {
    return '';
  }

  try {
    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
  } catch {
    return raw;
  }
};

const customAnswersToRows = (
  record: Record<string, string>,
): CustomAnswerRow[] =>
  Object.entries(record).map(([key, value]) => ({ key, value }));

const rowsToCustomAnswers = (
  rows: CustomAnswerRow[],
): Record<string, string> => {
  const record: Record<string, string> = {};
  for (const { key, value } of rows) {
    const trimmedKey = key.trim();
    if (trimmedKey) {
      record[trimmedKey] = value;
    }
  }
  return record;
};

type PendingDiscardAction =
  | { kind: 'close' }
  | { kind: 'replay' }
  | { kind: 'navigate' };

interface DiscardCallbacks {
  onClose?: () => void;
  onPendingNavigationResolved?: (confirmed: boolean) => void;
  onReplayOnboarding?: () => void;
}

const runDiscardAction = (
  action: PendingDiscardAction | null,
  confirmed: boolean,
  callbacks: DiscardCallbacks,
) => {
  if (action?.kind === 'navigate') {
    callbacks.onPendingNavigationResolved?.(confirmed);
    return;
  }

  if (!confirmed) {
    return;
  }

  if (action?.kind === 'replay') {
    callbacks.onReplayOnboarding?.();
  } else {
    callbacks.onClose?.();
  }
};

const hydrateFromProfile = (
  profile: Profile,
  setters: {
    setDiscord: (v: string) => void;
    setExperienceYears: (v: string) => void;
    setLinkedinCookie: (v: string) => void;
    setLocationPreference: (v: string) => void;
    setMaxScore: (v: number) => void;
    setMaxTailor: (v: number) => void;
    setMinSalary: (v: string) => void;
    setName: (v: string) => void;
    setOffpeakMin: (v: number) => void;
    setPeakEndHour: (v: number) => void;
    setPeakMin: (v: number) => void;
    setPeakStartHour: (v: number) => void;
    setResumeMd: (v: string | null) => void;
    setResumeFileName: (v: string | null) => void;
    setResumeFilePath: (v: string | null) => void;
    setResumeRaw: (v: string | null) => void;
    setScoringModel: (v: string) => void;
    setScrapers: (v: ScraperSource[]) => void;
    setDisableWeekendScans: (v: boolean) => void;
    setTailoringModel: (v: string) => void;
    setTargetRoles: (v: string) => void;
    setThreshold: (v: number) => void;
    setTimezone: (v: string) => void;
  },
) => {
  setters.setName(profile.name ?? '');
  setters.setExperienceYears(
    profile.experience_years != null ? String(profile.experience_years) : '',
  );
  setters.setMinSalary(
    profile.min_salary != null ? String(profile.min_salary) : '',
  );
  setters.setLocationPreference(profile.location_preference ?? '');
  setters.setTargetRoles(parseArrayField(profile.target_roles));
  setters.setLinkedinCookie(profile.linkedin_cookie ?? '');
  setters.setResumeMd(profile.resume_md);
  setters.setResumeFileName(profile.resume_file_name);
  setters.setResumeFilePath(profile.resume_file_path);
  setters.setResumeRaw(profile.resume_raw);
  setters.setDiscord(profile.discord_webhook ?? '');
  setters.setThreshold(profile.match_threshold ?? DEFAULT_MATCH_THRESHOLD);
  setters.setPeakMin(profile.peak_interval_min ?? DEFAULT_PEAK_MIN);
  setters.setOffpeakMin(profile.offpeak_interval_min ?? DEFAULT_OFFPEAK_MIN);
  setters.setPeakStartHour(profile.peak_start_hour ?? DEFAULT_PEAK_START_HOUR);
  setters.setPeakEndHour(profile.peak_end_hour ?? DEFAULT_PEAK_END_HOUR);
  setters.setTimezone(profile.timezone ?? DEFAULT_TIMEZONE);
  setters.setDisableWeekendScans(profile.disable_weekend_scans === 1);
  setters.setMaxScore(profile.max_score_per_cycle ?? DEFAULT_MAX_SCORE);
  setters.setMaxTailor(profile.max_tailor_per_cycle ?? DEFAULT_MAX_TAILOR);
  setters.setScoringModel(profile.scoring_model ?? DEFAULT_SCORING_MODEL);
  setters.setTailoringModel(profile.tailoring_model ?? DEFAULT_TAILORING_MODEL);
  setters.setScrapers(
    applyDisabledScrapers(
      SCRAPER_SOURCES,
      parseDisabledScrapers(profile.disabled_scrapers),
    ),
  );
};

interface UseSettingsFormParams {
  onClose?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onPendingNavigationResolved?: (confirmed: boolean) => void;
  onReplayOnboarding?: () => void;
  pendingNavigation?: ShellView | null;
}

export const useSettingsForm = ({
  onClose,
  onDirtyChange,
  onPendingNavigationResolved,
  onReplayOnboarding,
  pendingNavigation,
}: UseSettingsFormParams) => {
  const { data, isLoading } = useQuery(profileQueryOptions());
  const updateProfile = useUpdateProfile();
  const uploadResume = useUploadResume();
  const profile = data?.profile ?? null;

  const [name, setName] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [minSalary, setMinSalary] = useState('');
  const [locationPreference, setLocationPreference] = useState('');
  const [targetRoles, setTargetRoles] = useState('');
  const [linkedinCookie, setLinkedinCookie] = useState('');
  const [resumeMd, setResumeMd] = useState<string | null>(null);
  const [resumeFileName, setResumeFileName] = useState<string | null>(null);
  const [resumeFilePath, setResumeFilePath] = useState<string | null>(null);
  const [resumeRaw, setResumeRaw] = useState<string | null>(null);

  const [scrapers, setScrapers] = useState<ScraperSource[]>(SCRAPER_SOURCES);
  const [threshold, setThreshold] = useState(DEFAULT_MATCH_THRESHOLD);
  const [peakMin, setPeakMin] = useState(DEFAULT_PEAK_MIN);
  const [offpeakMin, setOffpeakMin] = useState(DEFAULT_OFFPEAK_MIN);
  const [peakStartHour, setPeakStartHour] = useState(DEFAULT_PEAK_START_HOUR);
  const [peakEndHour, setPeakEndHour] = useState(DEFAULT_PEAK_END_HOUR);
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [disableWeekendScans, setDisableWeekendScans] = useState(false);
  const [scoringModel, setScoringModel] = useState(DEFAULT_SCORING_MODEL);
  const [tailoringModel, setTailoringModel] = useState(DEFAULT_TAILORING_MODEL);
  const [maxScore, setMaxScore] = useState(DEFAULT_MAX_SCORE);
  const [maxTailor, setMaxTailor] = useState(DEFAULT_MAX_TAILOR);
  const [discord, setDiscord] = useState('');
  const [showLI, setShowLI] = useState(false);

  const [applicationAnswers, setApplicationAnswers] =
    useState<ApplicantAnswers>(() => ApplicantAnswersSchema.parse({}));
  const [customAnswerRows, setCustomAnswerRows] = useState<CustomAnswerRow[]>(
    [],
  );
  const [employmentHistoryText, setEmploymentHistoryText] = useState('[]');
  const [referencesText, setReferencesText] = useState('[]');
  const [applicationAnswersError, setApplicationAnswersError] = useState<
    string | null
  >(null);

  const [dirty, setDirty] = useState(false);
  const [lastSavedAgo, setLastSavedAgo] = useState<number | null>(null);
  const [hydratedProfileId, setHydratedProfileId] = useState<number | null>(
    null,
  );
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pendingDiscardAction, setPendingDiscardAction] =
    useState<PendingDiscardAction | null>(null);
  const [employmentPopulating, setEmploymentPopulating] = useState(false);
  const [employmentOverwriteOpen, setEmploymentOverwriteOpen] = useState(false);
  const pendingEmploymentRef = useRef<ApplicantAnswers['employmentHistory']>(
    [],
  );

  const initialDirtyOnNavRef = useRef<boolean | null>(null);

  if (profile && hydratedProfileId !== profile.id) {
    setHydratedProfileId(profile.id);
    hydrateFromProfile(profile, {
      setDisableWeekendScans,
      setDiscord,
      setExperienceYears,
      setLinkedinCookie,
      setLocationPreference,
      setMaxScore,
      setMaxTailor,
      setMinSalary,
      setName,
      setOffpeakMin,
      setPeakEndHour,
      setPeakMin,
      setPeakStartHour,
      setResumeFileName,
      setResumeFilePath,
      setResumeMd,
      setResumeRaw,
      setScoringModel,
      setScrapers,
      setTailoringModel,
      setTargetRoles,
      setThreshold,
      setTimezone,
    });

    const parsedAnswers = parseApplicantAnswers(profile.application_answers);
    setApplicationAnswers(parsedAnswers);
    setCustomAnswerRows(customAnswersToRows(parsedAnswers.customAnswers));
    setEmploymentHistoryText(
      JSON.stringify(parsedAnswers.employmentHistory, null, 2),
    );
    setReferencesText(JSON.stringify(parsedAnswers.references, null, 2));
    setApplicationAnswersError(null);
  }

  useEffect(() => {
    if (lastSavedAgo == null) {
      return;
    }

    const start = Date.now() - lastSavedAgo * 1000;
    const interval = setInterval(() => {
      setLastSavedAgo(Math.floor((Date.now() - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [lastSavedAgo]);

  useEffect(() => {
    if (!onClose) {
      return;
    }

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || confirmDiscard) {
        return;
      }

      if (!dirty) {
        onClose();
        return;
      }

      setPendingDiscardAction({ kind: 'close' });
      setConfirmDiscard(true);
    };

    window.addEventListener('keydown', handleKey);

    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, dirty, confirmDiscard]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (pendingNavigation == null) {
      initialDirtyOnNavRef.current = null;
      return;
    }

    if (initialDirtyOnNavRef.current === null) {
      initialDirtyOnNavRef.current = dirty;
    }

    if (initialDirtyOnNavRef.current === false) {
      onPendingNavigationResolved?.(true);
    }
  }, [pendingNavigation, dirty, onPendingNavigationResolved]);

  const navigationPromptOpen = pendingNavigation != null && dirty;
  const discardOpen = confirmDiscard || navigationPromptOpen;
  const effectivePendingAction: PendingDiscardAction | null =
    navigationPromptOpen ? { kind: 'navigate' } : pendingDiscardAction;

  const formErrors: Record<string, string | string[]> = {};
  if (updateProfile.error) {
    formErrors.root = `Save failed: ${updateProfile.error.message}`;
  }
  if (uploadResume.error) {
    formErrors.resume = `Upload failed: ${uploadResume.error.message}`;
  }

  const requestDiscard = (action: PendingDiscardAction, run: () => void) => {
    if (dirty) {
      setPendingDiscardAction(action);
      setConfirmDiscard(true);
    } else {
      run();
    }
  };

  const handleClose = () =>
    requestDiscard({ kind: 'close' }, () => onClose?.());

  const handleReplayOnboarding = () =>
    requestDiscard({ kind: 'replay' }, () => onReplayOnboarding?.());

  const discardCallbacks: DiscardCallbacks = {
    onClose,
    onPendingNavigationResolved,
    onReplayOnboarding,
  };

  const handleConfirmDiscard = () => {
    setConfirmDiscard(false);
    setDirty(false);
    runDiscardAction(effectivePendingAction, true, discardCallbacks);
    setPendingDiscardAction(null);
  };

  const handleCancelDiscard = () => {
    setConfirmDiscard(false);
    runDiscardAction(effectivePendingAction, false, discardCallbacks);
    setPendingDiscardAction(null);
  };

  const mark =
    <T>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };

  const resumeAvailable = Boolean(
    profile?.resume_raw || profile?.resume_md || profile?.resume_file_name,
  );

  const applyPendingEmployment = (
    entries: ApplicantAnswers['employmentHistory'],
  ) => {
    mark(setEmploymentHistoryText)(JSON.stringify(entries, null, 2));
  };

  const populateEmploymentFromResume = async () => {
    setEmploymentPopulating(true);
    try {
      const response = await fetch('/api/profile/employment-history', {
        method: 'POST',
      });
      if (!response.ok) {
        const body = (await response.json()) as { error?: string };
        setApplicationAnswersError(
          body.error ?? 'Could not extract employment history.',
        );
        return;
      }
      const body = (await response.json()) as {
        employmentHistory: ApplicantAnswers['employmentHistory'];
      };
      setApplicationAnswersError(null);
      const entries = body.employmentHistory;
      let hasExisting = false;
      try {
        const parsed = JSON.parse(employmentHistoryText);
        hasExisting = Array.isArray(parsed) && parsed.length > 0;
      } catch {
        hasExisting = false;
      }
      if (hasExisting) {
        pendingEmploymentRef.current = entries;
        setEmploymentOverwriteOpen(true);
      } else {
        applyPendingEmployment(entries);
      }
    } finally {
      setEmploymentPopulating(false);
    }
  };

  const confirmEmploymentOverwrite = () => {
    applyPendingEmployment(pendingEmploymentRef.current);
    pendingEmploymentRef.current = [];
    setEmploymentOverwriteOpen(false);
  };

  const cancelEmploymentOverwrite = () => {
    pendingEmploymentRef.current = [];
    setEmploymentOverwriteOpen(false);
  };

  const onResumeDrop = (files: File[]) => {
    const [file] = files;

    if (!file) {
      return;
    }

    uploadResume.mutate(file, {
      onSuccess: (result) => {
        setResumeMd(null);
        setResumeRaw(result.text);
        setResumeFilePath(result.filePath);
        setResumeFileName(result.fileName);
        setDirty(true);
      },
    });
  };

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    accept: RESUME_ACCEPT,
    disabled: uploadResume.isPending,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: onResumeDrop,
  });

  const applyChanges = () => {
    let employmentHistory: ApplicantAnswers['employmentHistory'];
    let references: ApplicantAnswers['references'];

    try {
      employmentHistory = JSON.parse(employmentHistoryText || '[]');
      references = JSON.parse(referencesText || '[]');
    } catch {
      setApplicationAnswersError(
        'Employment history and references must be valid JSON arrays.',
      );
      return;
    }

    const answersCandidate = {
      ...applicationAnswers,
      customAnswers: rowsToCustomAnswers(customAnswerRows),
      employmentHistory,
      references,
    };

    const parsedAnswers = ApplicantAnswersSchema.safeParse(answersCandidate);
    if (!parsedAnswers.success) {
      setApplicationAnswersError(
        `Application answers are invalid: ${parsedAnswers.error.issues[0]?.message ?? 'check the JSON fields.'}`,
      );
      return;
    }
    setApplicationAnswersError(null);

    const disabledScraperIds = scrapers
      .filter((source) => !source.on)
      .map((source) => source.id);

    const payload: Partial<Profile> = {
      application_answers: JSON.stringify(parsedAnswers.data),
      disable_weekend_scans: disableWeekendScans ? 1 : 0,
      disabled_scrapers: JSON.stringify(disabledScraperIds),
      discord_webhook: discord || null,
      experience_years: experienceYears ? Number(experienceYears) : null,
      linkedin_cookie: linkedinCookie || null,
      location_preference: locationPreference || null,
      match_threshold: threshold,
      max_score_per_cycle: maxScore,
      max_tailor_per_cycle: maxTailor,
      min_salary: minSalary ? Number(minSalary) : null,
      name,
      offpeak_interval_min: offpeakMin,
      peak_end_hour: peakEndHour,
      peak_interval_min: peakMin,
      peak_start_hour: peakStartHour,
      resume_file_name: resumeFileName,
      resume_file_path: resumeFilePath,
      resume_md: resumeMd || resumeRaw || null,
      resume_raw: resumeRaw,
      scoring_model: scoringModel,
      tailoring_model: tailoringModel,
      target_roles: JSON.stringify(splitCsv(targetRoles)),
      timezone,
    };
    updateProfile.mutate(payload, {
      onSuccess: () => {
        setDirty(false);
        setLastSavedAgo(0);
      },
    });
  };

  const resetDefaults = () => {
    setScrapers(SCRAPER_SOURCES);
    setThreshold(DEFAULT_MATCH_THRESHOLD);
    setPeakMin(DEFAULT_PEAK_MIN);
    setOffpeakMin(DEFAULT_OFFPEAK_MIN);
    setPeakStartHour(DEFAULT_PEAK_START_HOUR);
    setPeakEndHour(DEFAULT_PEAK_END_HOUR);
    setTimezone(DEFAULT_TIMEZONE);
    setDisableWeekendScans(false);
    setMaxScore(DEFAULT_MAX_SCORE);
    setMaxTailor(DEFAULT_MAX_TAILOR);
    setScoringModel(DEFAULT_SCORING_MODEL);
    setTailoringModel(DEFAULT_TAILORING_MODEL);
    setDiscord('');
    // Resetting to defaults is itself an edit; the form remains dirty until
    // the user either applies the new defaults or discards them.
    setDirty(true);
  };

  const nextScanMin = useNextScanMinutes(
    timezone,
    peakStartHour,
    peakEndHour,
    peakMin,
    offpeakMin,
  );

  return {
    applicationAnswers,
    applicationAnswersError,
    applyChanges,
    cancelEmploymentOverwrite,
    confirmEmploymentOverwrite,
    customAnswerRows,
    dirty,
    disableWeekendScans,
    discardOpen,
    discord,
    employmentHistoryText,
    employmentOverwriteOpen,
    employmentPopulating,
    experienceYears,
    formErrors,
    getInputProps,
    getRootProps,
    handleCancelDiscard,
    handleClose,
    handleConfirmDiscard,
    handleReplayOnboarding,
    isLoading,
    lastSavedAgo,
    linkedinCookie,
    locationPreference,
    mark,
    maxScore,
    maxTailor,
    minSalary,
    name,
    nextScanMin,
    offpeakMin,
    openFileDialog,
    peakEndHour,
    peakMin,
    peakStartHour,
    populateEmploymentFromResume,
    profile,
    referencesText,
    resetDefaults,
    resumeAvailable,
    resumeFileName,
    scoringModel,
    scrapers,
    setApplicationAnswers,
    setCustomAnswerRows,
    setDisableWeekendScans,
    setDiscord,
    setEmploymentHistoryText,
    setExperienceYears,
    setLinkedinCookie,
    setLocationPreference,
    setMaxScore,
    setMaxTailor,
    setMinSalary,
    setName,
    setOffpeakMin,
    setPeakEndHour,
    setPeakMin,
    setPeakStartHour,
    setReferencesText,
    setScoringModel,
    setScrapers,
    setShowLI,
    setTailoringModel,
    setTargetRoles,
    setThreshold,
    setTimezone,
    showLI,
    tailoringModel,
    targetRoles,
    threshold,
    timezone,
    updateProfile,
    uploadResume,
  };
};
